import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Input, Modal, Skeleton, Tag } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faLockOpen } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import { Management, PlatformLock } from "../../api/Management";
import { useToasts } from "../../contexts/toasts";

/**
 * Blocco globale dei circoli (solo Superadmin): finché è attivo i gestori non
 * accedono al gestionale e i loro circoli non compaiono nell'app.
 */
export default function PlatformLockCard() {
  const { showToast } = useToasts();
  const [lock, setLock] = useState<PlatformLock | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    Management.getPlatformLock().then(
      ({ data }) => setLock(data),
      () =>
        showToast(
          "platform-lock-load-error",
          "Errore nel reperimento dello stato di blocco.",
          "",
          "danger"
        )
    );
  }, [showToast]);

  const apply = useCallback(
    (locked: boolean) => {
      setSaving(true);
      Management.setPlatformLock(locked, locked ? message : undefined).then(
        ({ data }) => {
          setLock(data);
          setSaving(false);
          setConfirmOpen(false);
          setMessage("");
          showToast(
            "platform-lock-success",
            locked ? "Circoli bloccati" : "Circoli sbloccati",
            "",
            "success"
          );
        },
        () => {
          setSaving(false);
          showToast(
            "platform-lock-error",
            "Errore nell'aggiornamento del blocco.",
            "",
            "danger"
          );
        }
      );
    },
    [message, showToast]
  );

  if (!lock) {
    return (
      <Card className="mb-4">
        <Skeleton active paragraph={{ rows: 1 }} />
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div>
          <h5 className="mb-1">
            Blocco circoli{" "}
            {lock.locked ? (
              <Tag color="red">Attivo</Tag>
            ) : (
              <Tag color="green">Non attivo</Tag>
            )}
          </h5>
          <small className="text-muted">
            {lock.locked
              ? `Bloccati dal ${dayjs(lock.lockedAt).format(
                  "DD/MM/YYYY HH:mm"
                )}: i gestori non accedono al gestionale e i circoli non compaiono nell'app.`
              : "Blocca l'accesso al gestionale a tutti i circoli e nascondili nell'app finché non li sblocchi."}
          </small>
        </div>
        {lock.locked ? (
          <Button
            type="primary"
            loading={saving}
            onClick={() => apply(false)}
          >
            <FontAwesomeIcon icon={faLockOpen} className="me-2" />
            Sblocca circoli
          </Button>
        ) : (
          <Button danger onClick={() => setConfirmOpen(true)}>
            <FontAwesomeIcon icon={faLock} className="me-2" />
            Blocca circoli
          </Button>
        )}
      </div>

      {lock.locked && lock.message && (
        <Alert
          className="mt-3"
          type="warning"
          message="Messaggio mostrato ai gestori"
          description={lock.message}
        />
      )}

      <Modal
        title="Bloccare tutti i circoli?"
        open={confirmOpen}
        okText="Blocca circoli"
        cancelText="Annulla"
        okButtonProps={{ danger: true, loading: saving }}
        onOk={() => apply(true)}
        onCancel={() => setConfirmOpen(false)}
      >
        <p>
          I gestori verranno disconnessi dal gestionale e non potranno
          rientrare; i loro circoli spariranno dall'app (mappa, ricerca e
          preferiti). Il tuo account non è interessato. Puoi sbloccare in
          qualsiasi momento.
        </p>
        <Input.TextArea
          rows={3}
          maxLength={300}
          showCount
          placeholder="Messaggio per i gestori (facoltativo)"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      </Modal>
    </Card>
  );
}
